import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { COLORS } from './tokens';
import { displayYear } from './planRecordView';

export type OrderEntry = { id: string; name: string };

export function finalizedToastMessage(firstName: string, year: string): string {
  return `${firstName}'s ${displayYear(year)} plan is finalized.`;
}

export function nextStudentAfter(order: OrderEntry[], studentId: string): OrderEntry | null {
  const i = order.findIndex((entry) => entry.id === studentId);
  if (i < 0) return null;
  return order[i + 1] ?? null;
}

export function FinalizeDialog({
  open,
  firstName,
  year,
  prevYear,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  firstName: string;
  year: string;
  prevYear: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} role="alertdialog" aria-labelledby="finalize-plan-title" PaperProps={{ sx: { borderRadius: 4, maxWidth: 560, p: 1 } }}>
      <DialogTitle id="finalize-plan-title" sx={{ fontSize: 24, fontWeight: 600, lineHeight: '32px', color: COLORS.ink, pb: 1 }}>
        {`Finalize ${firstName}'s ${displayYear(year)} plan?`}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.ink }}>
          {`This saves the plan as ${firstName}'s plan for ${displayYear(year)}. Teachers will see it as final, and you can share and print it. To change it later, revise it: this version stays on record.`}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.muted }}>
          {`${firstName}'s ${displayYear(prevYear)} plan stays saved and can still be viewed. Notes are not affected.`}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.ink }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, bgcolor: COLORS.navy, '&:hover': { bgcolor: '#23236F' } }}>
          Finalize plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FinalizedToast({
  message,
  next,
  onNext,
  onClose,
}: {
  message: string;
  next: OrderEntry | null;
  onNext: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} autoHideDuration={6000} onClose={(_, reason) => reason !== 'clickaway' && onClose()}>
      <Box role="status" sx={{ width: 560, maxWidth: '90vw', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1, borderRadius: 2, bgcolor: COLORS.ink, color: '#FFFFFF' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 16, lineHeight: '24px' }}>{message}</Typography>
        </Box>
        {next && (
          <Button onClick={() => onNext(next.id)} endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: '#FFFFFF', flexShrink: 0 }}>
            {`Next: ${next.name}`}
          </Button>
        )}
      </Box>
    </Snackbar>
  );
}
